import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectCurrency, selectLocale } from '~/components/account/ducks';
import FlowBlocker from '~/components/authentication/flow-blocker';
import { limitsConst } from '~/components/payment/common/lib/utils/constants';
import { skipAmountSuggestion } from '~/components/payment/deposit-beta/utils';
import useTranslations from '~/components/translation/use-translations.ts';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import { getorientation } from '~/utils/style-utils';

import QDAmountContainer from './components/quick-deposit-amount-screen';
import { QDKYCScreen, QDRGScreen, QDSOWScreen } from './components/quick-deposit-secondary-views';
import { API_ERROR, API_LOADING, API_SUCCESS, LOADING, textKeysQDContent as textKeys } from './utils/constants';
import { providerLimitInfo } from './utils/qd-helpers';
import { useInstantDepositInfo, useQDNotifications } from './utils/use-quick-deposit-hooks';
import SchufaCreditLimitBanner from '~/components/payment/deposit/components/schufa-credit-limit-banner';

const Wrapper = styled.div`
	background-color: rgba(0, 0, 0, 0.4);
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
	height: 100%;

	${getorientation.landscape`
		max-width:  350px;
		margin: auto;
	`}
`;

const Content = styled.div`
	display: flex;
	background-color: var(--quick-deposit-modal-bg);
	flex-direction: column;
	margin: 0 24px;
	padding: 20px;
	align-items: center;
	border-radius: 12px;
	min-width: 280px;

	${getorientation.landscape`
		margin:  0;
		padding: 10px 20px;
	`}
`;

const MainContent = ({
	addView,
	hideView,
	views,
	updateViews,
	isLandscape,
	setKyc,
	redirectToPayments,
	redirectToSow
}) => {
	const locale = useSelector(selectLocale);
	const currency = useSelector(selectCurrency);
	const skipSuggestedAmount = skipAmountSuggestion();
	const instantDepositData = useInstantDepositInfo();
	const closeCurrentContext = useCloseCurrentContext();
	const { showErrorNotification } = useQDNotifications();
	const { isLoadingTranslations, translations: t } = useTranslations(textKeys);

	const [updateAmountVal, setAmount] = useState('');
	const [selectedMethod, setSelectedMethod] = useState(null);
	const [showKYCmessage, setshowKYCmessage] = useState(false);
	const [crossLimitAttempted, setCrossLimit] = useState(false);
	const [showRGmessageLimit, setRGmessageLimit] = useState(false);
	const [lugasLimit, setlugasLimit] = useState(false);
	const [providerLimitsData, setProviderLimitsData] = useState({});
	const [schufaError, setSchufaError] = useState('');

	const { depositInfo, depositInfoStatus, error: infoError } = instantDepositData;
	const defaultSelected = depositInfo?.instantDepositInfoForMethods?.filter(item => item.selected)[0];
	const defaultProvider = defaultSelected || depositInfo?.instantDepositInfoForMethods[0];

	const handleRGClose = event => {
		if (event) event.stopPropagation();

		if (crossLimitAttempted) {
			setCrossLimit(false);
			setRGmessageLimit(false);
			setAmount(providerLimitsData?.sum_max);
		} else {
			closeCurrentContext();
		}
	};

	useEffect(() => {
		if (showKYCmessage) {
			return setKyc(selectedMethod?.depositKycData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showKYCmessage, setKyc]);

	useEffect(() => {
		const selectedMethodLimit = selectedMethod?.depositLimitsAndFeesData;

		setProviderLimitsData(providerLimitInfo(selectedMethodLimit, currency, locale));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedMethod]);

	useEffect(() => {
		if (isLoadingTranslations || depositInfoStatus === API_LOADING) {
			updateViews(addView(LOADING));
		} else {
			updateViews(hideView(LOADING));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingTranslations, depositInfoStatus]);

	useEffect(() => {
		if (depositInfoStatus === API_SUCCESS) {
			const { sum_min: min } = providerLimitsData;
			const { suggestedIncreasedAmount, lastDepositAmountPlayerCurrency } = depositInfo || {};
			const suggestedAmount = skipSuggestedAmount ? null : suggestedIncreasedAmount;
			const lastDepositAmount = suggestedAmount || lastDepositAmountPlayerCurrency;
			const lastDepositVal = Number(lastDepositAmount);

			setSelectedMethod(defaultProvider);
			setAmount(lastDepositVal < min ? min : lastDepositVal);
		}

		if (depositInfoStatus === API_ERROR) {
			const errMsg = infoError || t.SYSTEM_ERROR_GENERAL;

			showErrorNotification(errMsg);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [depositInfoStatus]);

	const renderContent = () => {
		switch (true) {
			case showKYCmessage:
				return (
					<QDKYCScreen
						translation={t}
						addView={addView}
						updateViews={updateViews}
						isLandscape={isLandscape}
						setshowKYCmessage={setshowKYCmessage}
					/>
				);

			case providerLimitsData?.sum_max === 0 && limitsConst[providerLimitsData?.responsibleGamingLimitType] === 7:
				return (
					<QDSOWScreen
						translation={t}
						isLandscape={isLandscape}
						handleClose={handleRGClose}
						redirectToSow={redirectToSow}
					/>
				);
			case showRGmessageLimit && limitsConst[providerLimitsData?.responsibleGamingLimitType] !== 7:
				return (
					<QDRGScreen
						translation={t}
						addView={addView}
						updateViews={updateViews}
						isLandscape={isLandscape}
						islugasLimit={lugasLimit}
						formattedLimit={providerLimitsData?.formattedLimit}
						handleClose={handleRGClose}
					/>
				);

			default:
				return (
					<QDAmountContainer
						views={views}
						addView={addView}
						hideView={hideView}
						updateViews={updateViews}
						setCrossLimit={setCrossLimit}
						selectedMethod={selectedMethod}
						updateAmountVal={updateAmountVal}
						setSelectedMethod={setSelectedMethod}
						setshowKYCmessage={setshowKYCmessage}
						setRGmessageState={setRGmessageLimit}
						setSchufaError={setSchufaError}
						setlugasLimit={setlugasLimit}
						instantDepositData={instantDepositData}
						providerLimitsData={providerLimitsData}
						redirectToPayments={redirectToPayments}
					/>
				);
		}
	};

	return (
		<FlowBlocker onCancel={closeCurrentContext}>
			{schufaError || providerLimitsData?.schufaLimitHit ? (
				<SchufaCreditLimitBanner
					schufaError={schufaError}
					setSchufaError={setSchufaError}
					onclose={closeCurrentContext}
				/>
			) : (
				<Wrapper data-at="qd-main-content">
					<Content title="main-content" className="u-relative">
						{renderContent()}
					</Content>
				</Wrapper>
			)}
		</FlowBlocker>
	);
};

export default MainContent;
